// KeyboardHelpDialog — Radix Dialog opened by "?" or by clicking the help
// button in ShortcutBar. Exposes a context so ShortcutBar can open it.
//
// The dialog scope ('global:help') is at the bottom of the keyboard scope
// stack — the LAST mounted lane (typically) sits on top, so "?" only fires
// here when no other scope claims it. We register a *separate* document-level
// "?" listener inside this component so the help dialog is reachable from any
// lane (since the scope-stack model would otherwise hide our listener).

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { cn } from '@/lib/cn';

interface ShortcutItem {
  key: string;
  label: string;
}

interface KeyboardHelpContextValue {
  open: () => void;
  close: () => void;
  setItems: (items: ShortcutItem[]) => void;
}

const KeyboardHelpContext = createContext<KeyboardHelpContextValue | null>(null);

export function useKeyboardHelp(): KeyboardHelpContextValue {
  const ctx = useContext(KeyboardHelpContext);
  if (!ctx) {
    // No-op when no provider mounted — keeps ShortcutBar usable in isolation.
    return { open: () => {}, close: () => {}, setItems: () => {} };
  }
  return ctx;
}

interface KeyboardHelpDialogProps {
  initialItems?: ShortcutItem[];
  children?: ReactNode;
}

export function KeyboardHelpDialog({
  initialItems = [],
  children,
}: KeyboardHelpDialogProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<ShortcutItem[]>(initialItems);

  const openHelp = useCallback(() => setOpen(true), []);
  const closeHelp = useCallback(() => setOpen(false), []);

  // Document-level "?" listener — bypasses the scope stack so help is always
  // reachable. Bails on input/textarea/[contenteditable] + meta/ctrl/alt
  // (matching the same rules as the lane scope listener).
  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      if (e.key !== '?') return;
      const t = e.target;
      if (t instanceof HTMLElement) {
        if (/^(input|textarea|select)$/i.test(t.tagName)) return;
        if (t.isContentEditable) return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      e.preventDefault();
      setOpen(true);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const ctxValue: KeyboardHelpContextValue = {
    open: openHelp,
    close: closeHelp,
    setItems,
  };

  return (
    <KeyboardHelpContext.Provider value={ctxValue}>
      {children}
      <Dialog.Root open={open} onOpenChange={setOpen}>
        <Dialog.Portal>
          <Dialog.Overlay
            className={cn(
              'fixed inset-0 z-overlay bg-black/50 backdrop-blur-sm',
              'data-[state=open]:animate-in data-[state=closed]:animate-out',
            )}
          />
          <Dialog.Content
            className={cn(
              'fixed left-1/2 top-1/2 z-overlay -translate-x-1/2 -translate-y-1/2',
              'w-[min(90vw,28rem)] rounded-lg bg-surface-3 p-6 shadow-soft',
            )}
          >
            <Dialog.Title className="text-base font-semibold text-fg-default">
              Keyboard shortcuts
            </Dialog.Title>
            <Dialog.Description className="mt-1 text-sm text-fg-muted">
              Press the indicated key to perform an action. Esc closes this dialog.
            </Dialog.Description>
            <ul className="mt-4 space-y-2">
              {items.length === 0 ? (
                <li className="text-sm text-fg-muted">No shortcuts registered for this lane.</li>
              ) : (
                items.map((item) => (
                  <li key={item.key} className="flex items-center justify-between gap-3 text-sm">
                    <span className="text-fg-default">{item.label}</span>
                    <kbd className="rounded border border-border-subtle bg-surface-0 px-2 py-0.5 font-mono text-xs">
                      {item.key}
                    </kbd>
                  </li>
                ))
              )}
            </ul>
            <div className="mt-4 flex justify-end">
              <Dialog.Close className="rounded bg-accent-9 px-3 py-1.5 text-sm font-medium text-fg-on-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-surface-3">
                Close
              </Dialog.Close>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </KeyboardHelpContext.Provider>
  );
}
